/**
 * Rich type-ahead — a listbox of icon + title + description, over any text input.
 *
 * WHY IT EXISTS. A native <datalist> shows a line of text and nothing else, so a catalogue of
 * thirty-eight errands read as thirty-eight bare titles: one had to pick one to find out what it
 * was, which is choosing in order to understand rather than the other way round.
 *
 * WHAT IT DOES NOT DO. It never removes the fallback. The input keeps its `list=`, so a browser
 * without this script — or with it broken — still gets the native type-ahead and the form still
 * works. This layer only takes over the DISPLAY, and it only does so once it has run.
 *
 * WHERE THE WORDS COME FROM. The options are rendered server-side into a <template>, already
 * translated, with the terms to match on in `data-terms`. The script therefore never resolves an
 * i18n key and never guesses a language: it matches the strings it was handed.
 */
import { logger } from '@hyperio/core/base.js';

/** Air left between the bottom of the list and the button it must not reach. */
const CLEARANCE = 8;
/** Below this the list stops being a list; we accept the overlap rather than an unreadable popup. */
const MIN_LIST_HEIGHT = 120;

/**
 * The bar PINNED over the viewport at a given height, if there is one — a sticky navigation at the
 * top, a fixed consent notice at the bottom.
 *
 * A pinned bar paints over the popup whatever the z-index says, because the two sit in different
 * stacking contexts. Measured on the fastast landing: `.ui-navbar` is `position: sticky` with
 * `z-index: 30` and a child of <body>, while the list carries `z-index: 40` deep inside <main>,
 * which declares none — so the bar wins, and a list opening upwards ran underneath it with its
 * first row swallowed whole, `elementFromPoint` at that row answering `.ui-navbar-menu`. Such a bar
 * cannot be found by walking the form, so it is found by asking the browser what is painted there.
 */
function pinnedAt(x, y) {
    let node = document.elementFromPoint(x, y);
    while (node && node !== document.body && node !== document.documentElement) {
        const position = window.getComputedStyle(node).position;
        if (position === 'fixed' || position === 'sticky') {
            return node.getBoundingClientRect();
        }
        node = node.parentElement;
    }
    return null;
}

/** Fold accents and case away: someone typing « reservation » must find « Réserver ». */
const fold = (value) =>
    (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

class Suggest {
    constructor(input, template) {
        this.input = input;
        this.options = [...template.content.querySelectorAll('[data-value]')].map((node) => ({
            value: node.dataset.value,
            terms: fold(node.dataset.terms),
            title: (node.querySelector('.tinyform-suggest-title')?.textContent || '').trim(),
            icon: node.querySelector('.tinyform-suggest-icon')?.className || '',
            node,
        }));
        this.index = -1;

        // WHAT IS SHOWN AND WHAT IS SENT ARE TWO DIFFERENT THINGS. The wire carries the key —
        // `delivery-address` — because that is what the address is made of; the reader should see
        // « Adresse de livraison ». The visible field therefore gives up its `name` to a hidden one,
        // and it gives it up HERE, in the branch where this script runs: without the script the
        // field keeps its name and the native datalist still submits the key, as it always did.
        this.hidden = document.createElement('input');
        this.hidden.type = 'hidden';
        this.hidden.name = input.name;
        this.hidden.value = input.value;
        input.removeAttribute('name');
        input.after(this.hidden);
        // The leading glyph of the bar becomes the chosen errand's own — the second half of « show
        // me what I picked », and the half a datalist could never do.
        this.glyph = (input.closest('.tinyform-editor-bar') || input.parentElement)
            .querySelector('.tinyform-editor-bar-icon');
        this.defaultGlyph = this.glyph ? this.glyph.className : '';
        // The page comes back from a submission with the KEY in the field. Resolve it to its title
        // straight away, or the reader meets the very slug we just went to the trouble of hiding.
        this.show(input.value);

        this.list = document.createElement('ul');
        this.list.className = 'tinyform-suggest';
        this.list.setAttribute('role', 'listbox');
        this.list.hidden = true;
        this.list.id = `${template.id}-listbox`;

        // The popup is a sibling of the BAR, not of the input: the bar is the positioned box the
        // popup hangs from, and an input inside a flex row is not one.
        const anchor = input.closest('.tinyform-editor-bar') || input.parentElement;
        anchor.classList.add('has-suggest');
        anchor.appendChild(this.list);

        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-controls', this.list.id);
        input.setAttribute('aria-autocomplete', 'list');
        // The native popup would otherwise open UNDER ours, on top of it, on browsers that show
        // both. Dropping the attribute is what closes it — and it is dropped only here, in the
        // branch where this script is running and has something better to show.
        input.removeAttribute('list');

        input.addEventListener('input', () => {
            this.sync();
            this.open();
        });
        input.addEventListener('focus', () => this.open());
        input.addEventListener('keydown', (event) => this.onKey(event));
        input.addEventListener('blur', () => window.setTimeout(() => this.close(), 120));
        // Ce qui part sur le fil est arrêté ICI, au dernier moment, quel que soit le chemin —
        // le bouton, la touche Entrée, une soumission programmée. Sans ça, taper « assistance »
        // et cliquer sans avoir choisi envoyait « assistance », que le serveur ne connaît pas :
        // la page revenait à l'amorce avec un message, ce qui se lit comme un bouton qui ne fait
        // rien. Le mot désignait pourtant une intention et une seule.
        input.form?.addEventListener('submit', (event) => {
            // A submitter that opts out of validation means it. The form editor reposts on a
            // service change through such a submitter, to reshape the address field rather than to
            // build: holding that round trip to a settled errand would block the very exchange
            // meant to help choose one, and it blocked in silence.
            if (event.submitter && event.submitter.formNoValidate) {
                return;
            }
            if (!this.settle()) {
                // Refuser en silence, c'est ce qui se lit comme un bouton mort : la liste montrait
                // des candidates, on appuyait, et la page revenait identique. On le dit SUR PLACE,
                // par la validation du navigateur, plutôt qu'en repassant par le serveur pour se
                // faire répondre la même chose une seconde plus tard.
                event.preventDefault();
                this.open();
                this.input.setCustomValidity(this.input.dataset.suggestInvalid || ' ');
                this.input.reportValidity();
                this.input.focus();
            }
        });
        // Le message ne survit pas à la frappe suivante : sans ça, corriger sa saisie laisse un
        // champ marqué invalide que plus rien ne justifie.
        input.addEventListener('input', () => input.setCustomValidity(''));
        this.list.addEventListener('mousedown', (event) => {
            const option = event.target.closest('[data-value]');
            if (option) {
                event.preventDefault();     // keep the focus, so blur does not race the pick
                this.pick(option.dataset.value);
            }
        });
    }

    matches() {
        const needle = fold(this.input.value);
        if (!needle) {
            return this.options;
        }
        // Every word must appear somewhere, in any order: « avis note » finds « Laisser un avis »
        // whichever way round it is typed.
        const words = needle.split(/\s+/);
        return this.options.filter((option) => words.every((word) => option.terms.includes(word)));
    }

    open() {
        const found = this.matches();
        this.list.replaceChildren();
        found.forEach((option, position) => {
            const row = option.node.cloneNode(true);
            row.id = `${this.list.id}-${position}`;
            this.list.appendChild(row);
        });
        this.list.hidden = found.length === 0;
        this.input.setAttribute('aria-expanded', String(!this.list.hidden));
        this.index = -1;
        this.highlight();
        if (!this.list.hidden) {
            this.keepClearOfWhatFollows();
        }
    }

    /**
     * THE POPUP MUST NOT COVER WHAT IT SITS ON TOP OF, and that is not a matter of taste: a click
     * aimed at something underneath and landing on the list is not ignored, it PICKS. Measured on
     * the form generator — type « request », five errands match, the list runs from the bar down
     * past the button forty pixels below it, and clicking the button silently fills the field with
     * « Request a callback » and builds nothing. Click again and the tool builds an errand nobody
     * chose. From the outside that reads exactly as « the button does not work, and some errands
     * are broken ».
     *
     * It used to keep clear of the SUBMIT BUTTONS alone, which was right when the button was the
     * only thing below the field. A second question — where the answers go — later slid in
     * between, and the list swallowed it whole: measured on the landing, 27 rows running 581→764
     * over a select sitting 675→723, 48 px of overlap across its full 646 px, and
     * `elementFromPoint` at the select's own centre answering a suggestion row. Choosing the
     * destination was therefore impossible while the list was open, and trying picked an errand
     * instead. So the obstacle is no longer a kind of element but a POSITION: whatever follows the
     * question this field belongs to, plus the form's verbs wherever they sit.
     *
     * When there is not enough room below to show a usable list, it opens UPWARDS rather than
     * shrinking into a slit — a two-line popup over a question you can no longer click is the
     * worst of both. If neither side has room, the roomier one wins and the list scrolls.
     */
    keepClearOfWhatFollows() {
        this.list.style.maxHeight = '';
        this.list.classList.remove('opens-upwards');
        // Out of its own way while it measures: `elementFromPoint` would otherwise answer the list
        // itself over the very bar we are looking for.
        this.list.style.pointerEvents = 'none';
        const anchor = this.input.closest('.tinyform-editor-bar') || this.input.parentElement;
        const bar = anchor.getBoundingClientRect();
        const centre = (bar.left + bar.right) / 2;

        const ceilings = [];
        // Whatever comes after the question this field belongs to.
        const next = this.input.closest('.tinyform-editor-pick')?.nextElementSibling;
        if (next) {
            ceilings.push(next.getBoundingClientRect().top);
        }
        // …and the form's verbs, wherever they sit. A hidden control measures as a zero-sized box
        // at the top of the document, so it never survives the filter below.
        (this.input.form?.querySelectorAll('button[type="submit"]') || []).forEach((button) => {
            ceilings.push(button.getBoundingClientRect().top);
        });
        // …and anything pinned over the foot of the window, which belongs to no form at all.
        const footer = pinnedAt(centre, window.innerHeight - 1);
        if (footer) {
            ceilings.push(footer.top);
        }
        // Read while still out of the way, and used further down: putting the list back first would
        // let it answer for the header on the very next measurement.
        const header = pinnedAt(centre, 1);
        this.list.style.pointerEvents = '';

        const below = ceilings.filter((edge) => edge > bar.bottom);
        const roomBelow = below.length ? Math.min(...below) - bar.bottom - CLEARANCE : Infinity;
        if (roomBelow >= MIN_LIST_HEIGHT) {
            if (Number.isFinite(roomBelow)) {
                this.list.style.maxHeight = `${roomBelow}px`;
            }
            return;
        }

        const roomAbove = bar.top - (header ? header.bottom : 0) - CLEARANCE;
        if (roomAbove > roomBelow) {
            this.list.classList.add('opens-upwards');
            this.list.style.maxHeight = `${Math.max(roomAbove, 0)}px`;
            return;
        }
        this.list.style.maxHeight = `${Math.max(roomBelow, 0)}px`;
    }

    close() {
        this.list.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.input.removeAttribute('aria-activedescendant');
    }

    highlight() {
        [...this.list.children].forEach((row, position) => {
            const active = position === this.index;
            row.classList.toggle('is-active', active);
            row.setAttribute('aria-selected', String(active));
            if (active) {
                this.input.setAttribute('aria-activedescendant', row.id);
                row.scrollIntoView({ block: 'nearest' });
            }
        });
        if (this.index < 0) {
            this.input.removeAttribute('aria-activedescendant');
        }
    }

    /** The option whose TITLE the reader typed, when they typed one exactly. */
    byTitle(text) {
        const needle = fold(text);
        return this.options.find((option) => fold(option.title) === needle);
    }

    byValue(value) {
        return this.options.find((option) => option.value === value);
    }

    /**
     * What goes on the wire after a keystroke. Typing the key itself still works — it is what the
     * field has always accepted — and typing a title exactly is resolved to its key rather than
     * sent as prose, which would come back « no such errand » for a word the reader read on screen.
     */
    sync() {
        const typed = this.input.value.trim();
        const named = this.byTitle(typed);
        this.hidden.value = named ? named.value : typed;
        this.paint(named || this.byValue(typed));
    }

    /**
     * Ce qui part sur le fil, arrêté au moment de partir. Déjà une clé connue : on n'y touche
     * pas. Sinon, et s'il ne reste QU'UNE suggestion, c'est elle — un mot qui ne désigne plus
     * qu'une chose a été choisi, même sans clic. Deux candidates ou plus, on n'invente pas : le
     * texte part tel quel et le serveur répond ce qu'il a à répondre.
     */
    settle() {
        // RIEN DE TAPE N'EST PAS UNE AMBIGUITE. Un champ vide fait correspondre TOUTES les options,
        // donc le garde ci-dessous le lisait comme « plusieurs candidates » et refusait la
        // soumission en demandant de choisir dans la liste — un reproche pour quelque chose que
        // personne n'a fait. Inatteignable tant que le bouton de construction etait le seul chemin,
        // puisqu'il reste ferme sur un champ vide ; la liste des services, qui reposte au
        // changement, l'atteint. Vide, on laisse partir : le serveur repond deja l'amorce, sans
        // erreur, ce qui est exactement ce qu'on veut ici.
        if (!this.input.value.trim()) {
            return true;
        }
        if (this.byValue(this.hidden.value)) {
            return true;
        }
        const found = this.matches();
        if (found.length === 1) {
            this.hidden.value = found[0].value;
            this.input.value = found[0].title;
            this.paint(found[0]);
            return true;
        }
        // Plusieurs candidates, ou aucune : on n'invente pas à la place du lecteur. Deux intentions
        // peuvent partager un mot — « catégorie » désigne celle qu'on crée ET celle que porte un
        // article — et prendre la première construirait un autre formulaire que celui voulu, sans
        // que rien ne le dise.
        return false;
    }

    /** Display an already-chosen value as its title and its glyph. */
    show(value) {
        const option = this.byValue(value);
        if (option) {
            this.input.value = option.title;
        }
        this.paint(option);
    }

    paint(option) {
        if (this.glyph) {
            this.glyph.className = option && option.icon
                ? `${option.icon.replace('tinyform-suggest-icon', '').trim()} tinyform-editor-bar-icon`
                : this.defaultGlyph;
        }
    }

    pick(value) {
        const option = this.byValue(value);
        this.hidden.value = value;
        this.input.value = option ? option.title : value;
        this.paint(option);
        this.close();
        // SAYING SO OUT LOUD. Setting `value` from script fires nothing, so everything watching the
        // form for completeness never heard that the one required field had just been answered:
        // `form-required.js` kept the submit shut, and picking an errand from the list — the very
        // gesture this list exists for — led to a button that looked dead and did nothing when
        // pressed. Measured on the landing: field « Contact », key `contact`, form complete, button
        // still `disabled`. Typing hid it, because typing fires its own events; the list could not.
        this.announce();
        // Filled, not submitted: the second question — where the answers go — sits right below, and
        // submitting here would answer for someone who has not read it yet.
        this.input.focus();
    }

    /**
     * Tell the form what a script just changed. Emitted from the HIDDEN twin rather than the visible
     * field: the visible one carries this module's own `input` listener, and firing there would
     * reopen the list we have just closed.
     */
    announce() {
        this.hidden.dispatchEvent(new Event('change', { bubbles: true }));
    }

    onKey(event) {
        const rows = [...this.list.children];
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            if (this.list.hidden) {
                this.open();
                return;
            }
            event.preventDefault();
            // -1 est le texte tapé, 0..n-1 les suggestions, et on boucle de l'un à l'autre.
            // L'arithmétique modulaire d'avant ramenait la flèche bas de -1 à -1 quand il n'y
            // avait qu'une seule suggestion : la liste était là, la touche ne descendait pas
            // dessus, et Entrée soumettait le texte brut.
            const step = event.key === 'ArrowDown' ? 1 : -1;
            const next = this.index + step;
            this.index = next < -1 ? rows.length - 1 : (next >= rows.length ? -1 : next);
            this.highlight();
        } else if (event.key === 'Enter' && this.index >= 0 && !this.list.hidden) {
            event.preventDefault();     // pick rather than submit, this once
            this.pick(rows[this.index].dataset.value);
        } else if (event.key === 'Escape' && !this.list.hidden) {
            event.preventDefault();
            this.close();
        }
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const inputs = document.querySelectorAll('input[data-suggest]');
    let wired = 0;
    inputs.forEach((input) => {
        const template = document.getElementById(input.dataset.suggest);
        // No template, no enhancement — and the native datalist is still there, untouched.
        if (template instanceof HTMLTemplateElement) {
            new Suggest(input, template);
            wired += 1;
        }
    });
    if (inputs.length) {
        logger.debug(`Rich suggest initialized (${wired}/${inputs.length})`);
    }
});
